import { create } from 'zustand';
import { AppStats, BuildSettings, BuildStatus, LogEntry } from '../types';
import { APP_CODE } from '../utils/codeTemplates';
import { timestamp } from '../utils/formatters';

interface AppState {
  /* Sidebar */
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebar: () => void;

  /* Stats */
  stats: AppStats;
  incrementBuilds: () => void;
  setApkSize: (size: string) => void;

  /* Build */
  buildStatus: BuildStatus;
  buildProgress: number;
  buildPhase: string;
  buildSettings: BuildSettings;
  buildLogs: LogEntry[];
  setBuildStatus: (status: BuildStatus) => void;
  setBuildProgress: (progress: number) => void;
  setBuildPhase: (phase: string) => void;
  updateBuildSettings: (patch: Partial<BuildSettings>) => void;
  addLog: (entry: LogEntry) => void;
  clearLogs: () => void;

  /* Editor */
  editorCode: string;
  setEditorCode: (code: string) => void;
}

const DEFAULT_SETTINGS: BuildSettings = {
  minify: true,
  debugMode: false,
  arch: 'arm64-v8a',
  target: 'release',
  signingEnabled: true,
  proguardEnabled: false,
};

const DEFAULT_STATS: AppStats = {
  builds: 12,
  lines: 4820,
  apkSize: '8.3',
  errors: 0,
  lastBuild: '2 hours ago',
};

const INIT_LOGS: LogEntry[] = [
  { type: 'accent',  text: '▶ FIN Contra v2.4.0 initialized', timestamp: timestamp() },
  { type: 'info',    text: '  Environment: Node.js 20.x | React Native 0.73 | Gradle 8.4' },
  { type: 'success', text: '✓ Android SDK detected at /usr/local/android-sdk' },
  { type: 'warn',    text: '⚠ Java 17+ required for Gradle 8.x builds' },
  { type: 'info',    text: '  Ready — drop a project or click Build APK to begin.' },
];

export const useAppStore = create<AppState>((set) => ({
  /* Sidebar */
  sidebarOpen: false,
  setSidebarOpen: (open) => set({ sidebarOpen: open }),
  toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),

  /* Stats */
  stats: DEFAULT_STATS,
  incrementBuilds: () =>
    set((s) => ({ stats: { ...s.stats, builds: s.stats.builds + 1, lastBuild: 'Just now' } })),
  setApkSize: (size) =>
    set((s) => ({ stats: { ...s.stats, apkSize: size } })),

  /* Build */
  buildStatus: 'idle',
  buildProgress: 0,
  buildPhase: '',
  buildSettings: DEFAULT_SETTINGS,
  buildLogs: INIT_LOGS,
  setBuildStatus: (buildStatus) => set({ buildStatus }),
  setBuildProgress: (buildProgress) => set({ buildProgress }),
  setBuildPhase: (buildPhase) => set({ buildPhase }),
  updateBuildSettings: (patch) =>
    set((s) => ({ buildSettings: { ...s.buildSettings, ...patch } })),
  addLog: (entry) =>
    set((s) => ({ buildLogs: [...s.buildLogs, entry] })),
  clearLogs: () => set({ buildLogs: [] }),

  /* Editor */
  editorCode: APP_CODE,
  setEditorCode: (editorCode) => set({ editorCode }),
}));
